import React from 'react'

import styled from '@emotion/styled'

import { ArcadeIcon } from '@arcade/web/icons/react'

import { spacing } from '../../../theme'
import { Flex } from '../../layout/Flex'
import { Grid } from '../../layout/Grid'
import Stack from '../../layout/Stack'
import Text from '../../Text/Text'

export interface ListItem {
  Icon?: ArcadeIcon
  label: string
  body?: React.ReactNode
  value?: React.ReactNode
}

export interface ListUnorderedProps {
  type?: 'standard' | 'subtle' // Defaults to 'standard'
  size?: 'compact' | 'large' // Defaults to 'compact'
  items: ListItem[]
  // Renders a marker for every item: the item's icon when supplied, otherwise a
  // decorative bullet. When false (the default), icons only render if every item
  // has one, and icon-less items render no marker.
  showBullets?: boolean // Defaults to false
}

const PaddingTop = styled.div<{ isSubtle?: boolean }>`
  padding-top: ${({ theme, isSubtle }) => (isSubtle ? theme.spacing.xxs : '0')};
  color: ${({ theme }) => theme.arcadeColors.semantic.iconStandard};
`

// Occupies the same footprint as the icon and centers the bullet glyph so
// icon-less items line up with icon items in a mixed list.
const Bullet = styled.div<{ size: number }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${({ size }) => size}px;
  height: ${({ size }) => size}px;
`

const ListContainer = styled(Grid)<{ size: 'compact' | 'large' }>`
  ${({ size, theme }) => size === 'large' && `padding: ${theme.spacing.m} 0;`}
`

const ListUnordered = ({ type = 'standard', size = 'compact', items, showBullets = false }: ListUnorderedProps) => {
  if (!items.length) return null

  const numberOfColumns = items.some(item => 'value' in item) ? 2 : 1
  const hasBody = items.some(item => item.body)
  // Reserve the marker column when bullets are enabled, or when every item has
  // an icon (the legacy all-or-nothing icon behavior).
  const showMarkerColumn = showBullets || items.every(item => item.Icon)
  const listItemTemplateColumns = showMarkerColumn ? `${spacing.l} 1fr` : 'auto'
  const iconToContentGap = type === 'standard' ? 'm' : 'xs'

  const getRowGap = (): 'xs' | 'm' | 'l' | 'xl' => {
    if (size === 'large') {
      return hasBody ? 'xl' : 'l'
    }
    return hasBody ? 'm' : 'xs'
  }

  const getLabelTypography = (item: ListItem): 'labelMedium' | 'bodyMedium' => {
    if (type === 'subtle') {
      return 'bodyMedium'
    }
    // Standard type: use labelMedium if has body OR no value, otherwise bodyMedium
    if (item.body || !item.value) {
      return 'labelMedium'
    }
    return 'bodyMedium'
  }

  return (
    <ListContainer as="ul" rowGap={getRowGap()} size={size}>
      {items.map((item, index) => {
        const Icon =
          item.Icon &&
          styled(item.Icon)`
            display: block;
          `

        return (
          <Grid
            as="li"
            gridTemplateColumns={listItemTemplateColumns}
            columnGap={iconToContentGap}
            alignItems="flex-start"
            key={index}
            data-testid="unordered-list-item"
          >
            {Icon && showMarkerColumn ? (
              <PaddingTop isSubtle={type === 'subtle'} data-testid="unordered-list-item-icon">
                <Icon size={type === 'subtle' ? 16 : 24} />
              </PaddingTop>
            ) : showBullets ? (
              <PaddingTop aria-hidden="true" isSubtle={type === 'subtle'} data-testid="unordered-list-item-bullet">
                <Bullet size={type === 'subtle' ? 16 : 24}>•</Bullet>
              </PaddingTop>
            ) : null}
            <Text color="textStandard">
              <Flex justifyContent="space-between" gap={spacing.m} flexGrow={1}>
                <Stack space={spacing.empty}>
                  <Text typography={getLabelTypography(item)} textAlign="left">
                    {item.label}
                  </Text>
                  {item.body && (
                    <Text typography="bodySmall" textAlign="left">
                      {item.body}
                    </Text>
                  )}
                </Stack>
                {numberOfColumns === 2 && 'value' in item && (
                  <Text typography="labelMedium" textAlign="right">
                    {item.value}
                  </Text>
                )}
              </Flex>
            </Text>
          </Grid>
        )
      })}
    </ListContainer>
  )
}

export default ListUnordered
